import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { MessageCircle, PencilLine } from 'lucide-react'
import type { Conversation } from '@olimhub/shared'
import { messagesApi } from '@/api'
import { Avatar } from '@/components/profile/Avatar'
import { formatPostTime } from '@/lib/dates'
import { useAuthStore } from '@/store/auth.store'
import { NewMessageModal } from '@/components/messages/NewMessageModal'
import { ConversationSkeleton } from '@/components/common'

function ConversationItem({ conv }: { conv: Conversation }) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { account } = useAuthStore()
  const other = conv.otherAccount
  const last = conv.lastMessage
  const isFromMe = last.senderId === account?.id
  const hasUnread = conv.unreadCount > 0

  return (
    <button
      type="button"
      onClick={() => navigate(`/messages/${other.username}`)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        padding: '12px 16px',
        border: 'none',
        background: 'none',
        cursor: 'pointer',
        textAlign: 'start',
        borderBottom: '1px solid var(--border)',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-2)' }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'none' }}
    >
      <Avatar
        name={other.displayName || other.username}
        avatarUrl={other.avatarUrl}
        animalAvatar={other.animalAvatar}
        size="md"
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 8,
            marginBottom: 2,
          }}
        >
          <span
            style={{
              fontFamily: 'var(--font-sans)',
              fontWeight: 700,
              fontSize: 'var(--fs-sm)',
              color: 'var(--text-strong)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {other.displayName || other.username}
          </span>
          <span
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--fs-xs)',
              color: 'var(--text-faint)',
              flexShrink: 0,
            }}
          >
            {formatPostTime(last.createdAt)}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--fs-sm)',
              color: hasUnread ? 'var(--text-strong)' : 'var(--text-muted)',
              fontWeight: hasUnread ? 600 : 400,
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {isFromMe ? `${t('messages.you')}: ` : ''}
            {last.isDeleted ? <em style={{ color: 'var(--text-faint)' }}>{t('messages.message_deleted')}</em> : last.content}
          </span>
          {hasUnread && (
            <span
              style={{
                flexShrink: 0,
                minWidth: 18,
                height: 18,
                borderRadius: 'var(--radius-pill)',
                background: 'var(--danger)',
                color: '#fff',
                fontSize: 10,
                fontWeight: 700,
                fontFamily: 'var(--font-sans)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 4px',
              }}
            >
              {conv.unreadCount > 9 ? '9+' : conv.unreadCount}
            </span>
          )}
        </div>
      </div>
    </button>
  )
}

export function MessagesPage() {
  const { t } = useTranslation()
  const [composeOpen, setComposeOpen] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['conversations'],
    queryFn: () => messagesApi.getConversations(),
  })

  const conversations = data?.items ?? []

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: 'var(--bg)',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '14px 16px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--surface)',
          flexShrink: 0,
        }}
      >
        <span
          style={{
            flex: 1,
            fontFamily: 'var(--font-sans)',
            fontWeight: 700,
            fontSize: 'var(--fs-lg)',
            color: 'var(--text-strong)',
          }}
        >
          {t('messages.title')}
        </span>
        <button
          type="button"
          aria-label={t('messages.new_conversation')}
          onClick={() => setComposeOpen(true)}
          style={{
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            color: 'var(--brand)',
            padding: 6,
            display: 'flex',
            borderRadius: 'var(--radius-sm)',
          }}
        >
          <PencilLine size={22} />
        </button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {isLoading && (
          <>
            <ConversationSkeleton />
            <ConversationSkeleton />
            <ConversationSkeleton />
          </>
        )}

        {!isLoading && conversations.length === 0 && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 12,
              padding: '56px 32px',
              textAlign: 'center',
            }}
          >
            <MessageCircle size={48} strokeWidth={1.5} style={{ color: 'var(--text-faint)' }} />
            <p
              style={{
                margin: 0,
                fontFamily: 'var(--font-sans)',
                fontWeight: 600,
                fontSize: 'var(--fs-sm)',
                color: 'var(--text-strong)',
              }}
            >
              {t('messages.empty')}
            </p>
            <button
              type="button"
              onClick={() => setComposeOpen(true)}
              style={{
                marginTop: 8,
                padding: '10px 20px',
                border: 'none',
                borderRadius: 'var(--radius-pill)',
                background: 'var(--brand)',
                color: 'var(--on-brand)',
                fontFamily: 'var(--font-sans)',
                fontWeight: 700,
                fontSize: 'var(--fs-sm)',
                cursor: 'pointer',
              }}
            >
              {t('messages.new_conversation')}
            </button>
          </div>
        )}

        {conversations.map((conv) => (
          <ConversationItem key={conv.otherAccount.id} conv={conv} />
        ))}
      </div>

      <NewMessageModal open={composeOpen} onClose={() => setComposeOpen(false)} />
    </div>
  )
}
